import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './MailingListPage.scss';
import { easeOut, motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

// List of countries for the newsletter signup form
const countryList = [
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo (Congo-Brazzaville)", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czechia (Czech Republic)", "Democratic Republic of the Congo", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini (fmr. 'Swaziland')", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Holy See", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar (formerly Burma)", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine State", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States of America", "Uruguay", "Uzbekistan", "Vanuatu", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];

const MailingListPage = () => {
    const navigate = useNavigate();
    // Form state management
    const [form, setForm] = useState({
        email: '',
        country: '',
        shows: false,
        releases: false,
    });
    // Success state for form submission
    const [isSuccess, setIsSuccess] = useState(false);

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Handle form submission to MailerLite API
    const handleSubmit = async (e) => {
        e.preventDefault();

        const apiKey = process.env.REACT_APP_MAILERLITE_API_KEY;

        // Prepare data for API submission
        const data = {
            email: form.email,
            fields: {
                type_country: form.country,
                type_shows: form.shows ? 'Yes' : 'No',
                type_releases: form.releases ? 'Yes' : 'No',
            }
        };

        // Send data to MailerLite API
        const response = await fetch('https://connect.mailerlite.com/api/subscribers', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${apiKey}`,
            },
            body: JSON.stringify(data),
        });

        // Handle API response
        if (response.ok) {
            setIsSuccess(true);
            // Redirect to works page after 2 seconds
            setTimeout(() => {
                navigate('/works');
            }, 2000);
        } else {
            alert('There was an error. Please try again.');
        }
    };

    return (
        // Page container with fade animation
        <>
            <Helmet>
                <title>newsletter - nthng</title>
                <meta name="description" content="Newsletter - nthng" />
                <link rel="canonical" href="https://nthng.nl/newsletter" />
                <meta property="og:title" content="Newsletter - nthng" />
                <meta property="og:description" content="Subscribe to nthng newsletter for show and release updates" />
                <meta property="og:url" content="https://nthng.nl/newsletter" />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://nthng.nl/content/nthng_unfinished.webp" />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content="Newsletter - nthng" />
                <meta name="twitter:description" content="Newsletter - nthng" />
            </Helmet>
            <motion.div
                className="mailing-list-container"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: easeOut }}
            >
                {isSuccess ? (
                    // Success message with animation
                    <motion.div
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="success-message"
                    >
                        {'<3'}
                    </motion.div>
                ) : (
                    // Newsletter signup form

                    <form className="newsletter-form" onSubmit={handleSubmit}>
                        {/* Email input field */}
                        <input
                            className="newsletter-input"
                            type="email"
                            id="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            autoComplete="off"
                            placeholder="enter your email"
                            required
                        />
                        <div className="newsletter-underline" />

                        {/* Country selection dropdown */}
                        <select
                            className="newsletter-input"
                            id="country"
                            name="country"
                            value={form.country}
                            onChange={handleChange}
                            required
                        >
                            <option value="" disabled>select your country</option>
                            {countryList.map((country) => (
                                <option key={country} value={country}>{country}</option>
                            ))}
                        </select>
                        <div className="newsletter-underline" />

                        {/* Newsletter type selection boxes */}
                        <div className="newsletter-type-section">
                            <div
                                className={`newsletter-type-box ${form.shows ? 'selected' : ''}`}
                                onClick={() => setForm(prev => ({ ...prev, shows: !prev.shows }))}
                            >
                                shows
                            </div>
                            <div
                                className={`newsletter-type-box ${form.releases ? 'selected' : ''}`}
                                onClick={() => setForm(prev => ({ ...prev, releases: !prev.releases }))}
                            >
                                releases
                            </div>
                        </div>

                        {/* Submit button */}
                        <button className="newsletter-signup" type="submit">sign up</button>
                    </form>

                )}
            </motion.div>
        </>
    );
};

export default MailingListPage;
